// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import {
  LocalFireDepartmentRounded,
  WaterDropRounded,
} from '@mui/icons-material';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';

// api
import { useGetBasicEnergyAccountQuery } from '@/api/energy-analysis/basicEnergySlice';
import { useGetAccountByIdQuery } from '@/api/recalc/endpoints/accountsApiSlice';

const DASHBOARD_SERVICE_MAP = {
  DashboardPowerBasic: {
    id: 'DashboardPowerBasic',
    titleKey: 'dashboardCards.powerBasic.title',
    path: 'power-basic',
    icon: (
      <BoltRoundedIcon sx={(theme) => ({ color: theme.palette.power.main })} />
    ),
    descriptionKey: 'dashboardCards.powerBasic.description',
  },
  DashboardHeatBasic: {
    id: 'DashboardHeatBasic',
    titleKey: 'dashboardCards.heatBasic.title',
    path: 'heat-basic',
    icon: (
      <LocalFireDepartmentRounded
        sx={(theme) => ({ color: theme.palette.heat.main })}
      />
    ),
    descriptionKey: 'dashboardCards.heatBasic.description',
  },
  DashboardWaterBasic: {
    id: 'DashboardWaterBasic',
    titleKey: 'dashboardCards.waterBasic.title',
    path: 'water-basic',
    icon: (
      <WaterDropRounded sx={(theme) => ({ color: theme.palette.water.main })} />
    ),
    descriptionKey: 'dashboardCards.waterBasic.description',
  },
  DashboardCompressedAir: {
    id: 'DashboardCompressedAir',
    titleKey: 'dashboardCards.compressedAir.title',
    path: 'compressed-air',
    icon: (
      <AirRoundedIcon
        sx={(theme) => ({ color: theme.palette.compressedAir.main })}
      />
    ),
    descriptionKey: 'dashboardCards.compressedAir.description',
  },
};

/**
 * Hook: useDashboardsFromServices
 * - Reads accountId from route params (useParams())
 * - Fetches account via useGetAccountByIdQuery
 * - Maps account Services to dashboard entries using DASHBOARD_SERVICE_MAP
 *
 * Returns: { dashboards, isLoading, isFetching, error, account }
 */
export default function useDashboardsFromServices() {
  const { accountId } = useParams({ strict: false });
  const { t } = useTranslation('dashboards-common');

  const {
    data: account,
    isLoading,
    isFetching,
    error,
  } = useGetAccountByIdQuery(accountId, {
    skip: !accountId,
  });
  const { data: basicEnergySubscription } = useGetBasicEnergyAccountQuery(
    { account_id: accountId },
    {
      skip: !accountId,
    },
  );
  const dashboards = useMemo(() => {
    const services = Array.isArray(account?.Services) ? account.Services : [];
    const serviceNames = new Set(
      services.filter((s) => DASHBOARD_SERVICE_MAP[s.Name]).map((s) => s.Name),
    );

    const DashboardsWithReadyStatus = Object.values(DASHBOARD_SERVICE_MAP).map(
      (service) => {
        const base = {
          ...service,
          title: t(service.titleKey),
          description: t(service.descriptionKey),
        };
        switch (service.id) {
          case 'DashboardPowerBasic':
            return {
              ...base,
              isReady: Boolean(
                basicEnergySubscription?.LastPowerCacheTimestamp,
              ),
            };
          case 'DashboardHeatBasic':
            return {
              ...base,
              isReady: Boolean(basicEnergySubscription?.LastHeatCacheTimestamp),
            };
          case 'DashboardWaterBasic':
            return {
              ...base,
              isReady: Boolean(
                basicEnergySubscription?.LastWaterCacheTimestamp,
              ),
            };
          case 'DashboardCompressedAir':
            return {
              ...base,
              isReady: true,
            };
          default:
            return { ...base, isReady: true };
        }
      },
    );

    return DashboardsWithReadyStatus.filter((service) =>
      serviceNames.has(service.id),
    );
  }, [account, basicEnergySubscription, t]);

  return { dashboards, isLoading, isFetching, error, account };
}
